// Which identifying field a Take Order ticket must carry before it can be sent. Pure
// functions only: no React, no network, no module state.
//
// Mirrors admin-mobile-app/src/utils/requiredOrderFields.js. Keep the two byte-identical apart from this one header line, following the existing money.js / menuName.js / addableOrder.js / modifierSelection.js convention. A rule about which field is mandatory must not depend on which device the waiter picked up.
//
// WHY THIS RULE EXISTS. A ticket needs something to call it by, or it cannot be served:
// a dine-in ticket with no table has nowhere to take the food, and a to-go bag with no name
// on it has nobody to hand it to. So each order type has exactly ONE mandatory field.
//
// WHY IT IS A SHARED MODULE rather than a couple of inline conditions. The same rule drives
// four things that must never disagree: whether the send button is enabled, which label gets
// a red asterisk, which input gets a red outline, and which hint appears under the button. A
// disabled button with no marked field — or a marked field with an enabled button — is worse
// than either rule alone, because the waiter cannot tell what the screen wants.
//
// THE SERVER ENFORCES BOTH INDEPENDENTLY (`_handle_create_staff_order` in lambda/pos/admin.py).
// These functions are convenience; they exist so the screen can explain itself before a
// request is made, not to be the enforcement point.

/**
 * Order type -> the one field it requires.
 *
 * DINE_IN's table has been required, and server-enforced, since server-order-entry shipped.
 * TO_GO's customer name is the newer half.
 */
export const REQUIRED_FIELD_BY_ORDER_TYPE = {
  DINE_IN: 'tableLabel',
  TO_GO: 'customerName',
};

/**
 * Which mandatory field is still blank: `'tableLabel'`, `'customerName'` or `''`.
 *
 * Reads the field off `state` by the name in the map above, so a new order type is added in
 * exactly one place.
 *
 * ALWAYS `''` IN ADD_MODE. The target order already owns its type, table and customer, and
 * none of them are being set — requiring either would leave the update button permanently
 * dead for every addition, which is the bug this early return exists to prevent.
 *
 * An UNRECOGNISED order type requires nothing. Failing open is deliberate: an unexpected
 * value should not render the screen unusable with no indication of why, and the server
 * rejects a bad order type anyway.
 *
 * Whitespace counts as blank, or a single space would satisfy the rule and the chit would
 * print a ticket with no name on it — precisely the outcome being prevented.
 */
export function missingRequiredField(state) {
  if (!state || typeof state !== 'object') return '';
  if (state.addMode) return '';
  const field = REQUIRED_FIELD_BY_ORDER_TYPE[state.orderType];
  if (!field) return '';
  return String(state[field] == null ? '' : state[field]).trim() === '' ? field : '';
}

/** The i18n key describing what is missing, or `''`. Keeps the message choice in one place. */
export function missingFieldPromptKey(state) {
  const field = missingRequiredField(state);
  if (field === 'tableLabel') return 'takeOrder.tablePrompt';
  if (field === 'customerName') return 'takeOrder.customerNamePrompt';
  return '';
}
